import { useEffect, useMemo } from "react";
import { initialsOf } from "@/components/common/UserInitials";
import { useLocation } from "react-router-dom";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { ShieldCheck, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { AvatarBlock } from "@/components/settings/AccountSection";
import WeatherIcon, { getDayPeriod } from "@/components/WeatherIcon";
import { useAuth } from "@/context/AuthContext";

/**
 * Farewell screen shown after clicking « 👋 Bye bye » in the TopBar.
 *
 * Layout mirrors the login page:
 *   • Same wallpaper + centered vertical divider.
 *   • Left panel: identical branding (Logo, badge, tagline, footer copyright).
 *   • Right panel: big avatar (with a small sun/moon status badge), full name,
 *     recorded departure time, and a « Se reconnecter » button.
 *
 * The snapshot is passed via `navigate(..., { state })` from the TopBar handler
 * so the page can render even after the auth session was destroyed.
 */
export default function ByeBye() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const snapshot = useMemo(() => location.state || {}, [location.state]);

  // If someone lands here without a state (deep-link, refresh), send them
  // back where they belong: dashboard if still authenticated, login otherwise.
  useEffect(() => {
    if (!snapshot?.departure_time) {
      const t = setTimeout(
        () => navigate(user ? "/tableau-de-bord" : "/connexion", { replace: true }),
        20
      );
      return () => clearTimeout(t);
    }
  }, [snapshot, navigate, user]);

  const initials = useMemo(
    () => initialsOf(snapshot),
    [snapshot.first_name, snapshot.last_name]
  );

  // matinée / après-midi / soirée — drives icon + farewell text.
  const { label, isNight } = useMemo(() => {
    const d = snapshot.departure_iso ? new Date(snapshot.departure_iso) : new Date();
    return getDayPeriod(d);
  }, [snapshot.departure_iso]);
  const farewell = isNight
    ? "Bonne soirée !"
    : (label === "après-midi" ? "Bon après-midi !" : "Bonne matinée !");

  return (
    <div className="min-h-screen wallpaper flex relative" data-testid="bye-bye-page">
      {/* Short centered divider between the two columns — identical to Login */}
      <div
        aria-hidden="true"
        className="hidden lg:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px"
        style={{
          height: "45vh",
          background:
            "linear-gradient(to bottom, transparent 0%, #444444 50%, transparent 100%)",
        }}
      />

      {/* Left brand panel — pixel-identical to Login */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14">
        <Logo size={56} withWordmark />
        <div className="max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-vertec-blue/15 text-vertec-blue text-xs uppercase tracking-widest mb-6 font-semibold">
            <ShieldCheck size={14} /> Connexion sécurisée par code
          </div>
          <h1 className="text-5xl font-bold leading-tight mb-4">
            Together,<br />
            <span style={{ color: "#0078D4" }}>we build better.</span>
          </h1>
        </div>
        <div className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Vertec
        </div>
      </div>

      {/* Right farewell panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md flex flex-col items-center text-center animate-fade-in-up">
          <div className="lg:hidden mb-8"><Logo size={48} withWordmark /></div>

          {/* Big avatar + status badge (moon/sun) */}
          <div className="relative mb-6" data-testid="bye-bye-avatar-wrap">
            <AvatarBlock
              initials={initials}
              size={160}
              testid="bye-bye-avatar"
              seed={user?.id || user?.email || `${snapshot.first_name} ${snapshot.last_name}`}
            />
            <div
              className="absolute bottom-1 right-1 w-12 h-12 rounded-full flex items-center justify-center shadow-lg border-2 border-background bg-white"
              aria-label={isNight ? "Soir" : "Jour"}
              data-testid="bye-bye-time-badge"
            >
              <WeatherIcon isNight={isNight} size={28} />
            </div>
          </div>

          {/* Name */}
          <h2
            className="text-3xl font-semibold mb-2 tracking-tight"
            data-testid="bye-bye-name"
          >
            {snapshot.first_name} {snapshot.last_name}
          </h2>

          {/* Farewell + departure time */}
          <p className="text-lg text-muted-foreground mb-1" data-testid="bye-bye-farewell">
            {farewell}
          </p>
          <p className="text-sm text-muted-foreground mb-8" data-testid="bye-bye-departure">
            Départ enregistré à{" "}
            <span className="text-foreground font-semibold tabular-nums">
              {snapshot.departure_time || "—"}
            </span>
          </p>

          <Button
            onClick={() =>
              navigate(user ? "/tableau-de-bord" : "/connexion", { replace: true })
            }
            className="bg-vertec-blue hover:bg-vertec-blueHover text-white gap-2 px-6"
            data-testid="bye-bye-reconnect"
          >
            <LogIn size={16} /> Se reconnecter
          </Button>
        </div>
      </div>
    </div>
  );
}
